import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import i18n from '../i18n/i18n';
import { userLanguageService } from '../services/userLanguageService';

const SUPPORTED_LANGS = ['fr', 'en'] as const;
export type Lang = typeof SUPPORTED_LANGS[number];

function detectLang(): Lang {
    const browser = navigator.language.slice(0, 2).toLowerCase();
    return SUPPORTED_LANGS.includes(browser as Lang) ? (browser as Lang) : 'fr';
}

interface LanguageState {
    lang: Lang;
    setLang: (lang: Lang) => void;
    /** Called after authentication is established to seed the server-side preference from localStorage (F-01.3). */
    seedServerPreference: () => void;
}

export const useLanguageStore = create<LanguageState>()(
    persist(
        (set, get) => ({
            lang: detectLang(),
            setLang: (lang: Lang) => {
                void i18n.changeLanguage(lang);
                set({ lang });
                void userLanguageService.setLanguage(lang).catch(() => {
                    // F-01.4: language change takes effect locally; server persist failure is non-blocking.
                    // The next session start will retry via seedServerPreference.
                });
            },
            seedServerPreference: () => {
                const { lang } = get();
                void userLanguageService.setLanguage(lang).catch(() => {
                    // F-01.4: seed failure is silent; next session start retries.
                });
            },
        }),
        {
            name: 'language',
            onRehydrateStorage: () => (state) => {
                if (state) {
                    void i18n.changeLanguage(state.lang);
                }
            },
        }
    )
);
